import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  CreditCard,
  DollarSign,
  CheckCircle,
  Clock,
  XCircle,
  Calendar,
  Search,
  Filter,
  TrendingUp,
  ArrowUpRight,
  ArrowDownLeft,
  Wallet,
  Sparkles,
} from "lucide-react";
import PAYMENT_API from "../../data/api";

export const TransactionsPanelContent = ({ closePanel }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const paymentsPerPage = 10;

  const loadPayments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(PAYMENT_API.GET_ALL);
      setPayments(res.data.data || res.data);
    } catch (err) {
      console.error("Payment Load Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayments();
  }, []);

  // Filter payments
  const filteredPayments = payments.filter((payment) => {
    const matchesSearch =
      payment.paymentId?.toString().includes(searchTerm) ||
      payment.userId?.toString().includes(searchTerm) ||
      payment.fullName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || payment.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const indexOfLast = currentPage * paymentsPerPage;
  const indexOfFirst = indexOfLast - paymentsPerPage;
  const currentPayments = filteredPayments.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredPayments.length / paymentsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const total = payments.length;
  const successful = payments.filter((p) => p.status === "Completed").length;
  const pending = payments.filter((p) => p.status === "Pending").length;
  const failed = payments.filter((p) => p.status === "Failed").length;
  const totalRevenue = payments
    .filter((p) => p.status === "Completed")
    .reduce((sum, p) => sum + (p.paymentAmount || 0), 0);

  const getStatusConfig = (status) => {
    switch (status) {
      case "Completed":
        return {
          bg: "bg-green-100",
          text: "text-green-700",
          icon: <CheckCircle size={16} />,
          gradient: "from-green-500 to-emerald-500",
        };
      case "Pending":
        return {
          bg: "bg-yellow-100",
          text: "text-yellow-700",
          icon: <Clock size={16} />,
          gradient: "from-yellow-500 to-amber-500",
        };
      case "Failed":
        return {
          bg: "bg-red-100",
          text: "text-red-700",
          icon: <XCircle size={16} />,
          gradient: "from-red-500 to-rose-500",
        };
      default:
        return {
          bg: "bg-gray-100",
          text: "text-gray-700",
          icon: <CreditCard size={16} />,
          gradient: "from-gray-500 to-gray-600",
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1 }}
          className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <Wallet className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-800">Transactions</h2>
              <p className="text-gray-500 mt-1">
                Complete history of recent payments
              </p>
            </div>
          </div>
          <button
            onClick={closePanel}
            className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
          >
            <X size={22} className="text-gray-500" />
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
          {[
            {
              label: "Total Transactions",
              value: total,
              icon: CreditCard,
              gradient: "from-blue-500 to-cyan-500",
            },
            {
              label: "Successful",
              value: successful,
              icon: CheckCircle,
              gradient: "from-green-500 to-emerald-500",
            },
            {
              label: "Pending",
              value: pending,
              icon: Clock,
              gradient: "from-yellow-500 to-amber-500",
            },
            {
              label: "Failed",
              value: failed,
              icon: XCircle,
              gradient: "from-red-500 to-rose-500",
            },
            {
              label: "Total Revenue",
              value: `₹${totalRevenue.toLocaleString()}`,
              icon: TrendingUp,
              gradient: "from-purple-500 to-pink-500",
            },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-white rounded-2xl p-4 shadow-lg border border-gray-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{stat.value}</p>
                </div>
                <div
                  className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center`}
                >
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mt-6">
          <div className="relative flex-1">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search by ID, User, or Name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
            />
          </div>

          <div className="relative">
            <Filter
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-12 pr-8 py-3 bg-white border border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all appearance-none cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="Completed">Completed</option>
              <option value="Pending">Pending</option>
              <option value="Failed">Failed</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Transactions Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
      >
        {/* Table Header */}
        <div className="grid grid-cols-7 gap-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 font-semibold text-gray-600 text-sm">
          <span>Payment ID</span>
          <span>User</span>
          <span>Amount</span>
          <span>Status</span>
          <span>Date</span>
          <span>Method</span>
          <span>Type</span>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-gray-100">
          <AnimatePresence>
            {currentPayments.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-12 text-center"
              >
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <CreditCard size={40} className="text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  No Transactions Found
                </h3>
                <p className="text-gray-500">
                  {searchTerm || statusFilter !== "all"
                    ? "Try adjusting your search or filter"
                    : "Transactions will appear here"}
                </p>
              </motion.div>
            ) : (
              currentPayments.map((payment, index) => {
                const statusConfig = getStatusConfig(payment.status);
                return (
                  <motion.div
                    key={payment.paymentId}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.03 }}
                    className="grid grid-cols-7 gap-4 p-4 hover:bg-gray-50 transition-colors cursor-pointer items-center"
                  >
                    <span className="font-medium text-gray-800">
                      #{payment.paymentId}
                    </span>
                    <div>
                      <p className="font-medium text-gray-800">
                        {payment.fullName || "N/A"}
                      </p>
                      <p className="text-xs text-gray-500">ID: {payment.userId}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {payment.paymentType === "credit" ? (
                        <ArrowDownLeft
                          size={16}
                          className="text-green-500"
                        />
                      ) : (
                        <ArrowUpRight size={16} className="text-red-500" />
                      )}
                      <span className="font-semibold text-gray-800">
                        ₹{payment.paymentAmount?.toLocaleString()}
                      </span>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 w-max ${statusConfig.bg} ${statusConfig.text}`}
                    >
                      {statusConfig.icon}
                      {payment.status}
                    </span>
                    <span className="text-gray-500">
                      {payment.paidAt
                        ? new Date(payment.paidAt).toLocaleDateString()
                        : payment.createdAt
                        ? new Date(payment.createdAt).toLocaleDateString()
                        : "N/A"}
                    </span>
                    <span className="text-gray-600">
                      {payment.paymentMethod || "Card"}
                    </span>
                    <span className="text-gray-600 capitalize">
                      {payment.paymentType || "debit"}
                    </span>
                  </motion.div>
                );
              })
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Pagination */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex justify-center mt-8 gap-2"
        >
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((number) => (
            <button
              key={number}
              onClick={() => setCurrentPage(number)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                currentPage === number
                  ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg"
                  : "bg-white border text-gray-700 hover:bg-gray-100"
              }`}
            >
              {number}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default TransactionsPanelContent;
